import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchNetdataMetrics, NetdataServerData } from '../services/prometheusServiceNetdata';
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import type { DockerContainer } from './Dashboard';


const styles = {
    card: {
        backgroundColor: 'var(--metro-card-bg)',
        border: '1px solid var(--metro-card-border)',
        borderRadius: 'var(--card-radius)',
        padding: '20px',
        marginBottom: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    title: {
        margin: 0,
        color: 'var(--metro-text)'
    },
    version: {
        color: 'var(--metro-text-secondary)',
        fontSize: '14px'
    },
    chartsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '20px',
        marginTop: '20px'
    },
    chartContainer: {
        backgroundColor: 'var(--metro-dark-secondary)',
        padding: '15px',
        borderRadius: '8px',
        height: '300px'
    },
    error: {
        color: 'var(--metro-error)',
        padding: '20px',
        textAlign: 'center' as const
    },
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
    },
    metricCard: {
        backgroundColor: 'var(--metro-dark-secondary)',
        padding: '20px',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '10px'
    },
    metricTitle: {
        color: 'var(--metro-text-secondary)',
        fontSize: '14px',
        margin: 0
    },
    metricValue: {
        color: 'var(--metro-text)',
        fontSize: '24px',
        fontWeight: 600,
        margin: 0
    },
    metricSubValue: {
        color: 'var(--metro-text-secondary)',
        fontSize: '12px',
        margin: 0
    },
    progressBar: {
        width: '100%',
        height: '4px',
        backgroundColor: 'var(--metro-dark)',
        borderRadius: '2px',
        overflow: 'hidden'
    },
    progressFill: {
        height: '100%',
        backgroundColor: 'var(--metro-accent)',
        transition: 'width 0.3s ease'
    },
    ioStats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
        marginTop: '10px'
    },
    ioStat: {
        backgroundColor: 'var(--metro-dark)',
        padding: '8px',
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center'
    },
    ioValue: {
        color: 'var(--metro-accent)',
        fontSize: '16px',
        fontWeight: 600
    },
    ioLabel: {
        color: 'var(--metro-text-secondary)',
        fontSize: '12px'
    }
};

interface RawDockerContainer {
    value: number;
    labels?: {
        cgroup_name?: string;
        image?: string;
        [key: string]: string | undefined;
    };
}

interface DockerMetrics {
    name: string;
    cpu: number;
    image: string;
}

interface ChartData {
    time: string;
    value: number;
}

const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
};

const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatSpeed = (speed: number) => {
    if (speed < 1024) return `${speed.toFixed(1)} KiB/s`;
    return `${(speed / 1024).toFixed(1)} MiB/s`;
};

const formatGiB = (gib: number) => {
    if (gib < 1) return `${(gib * 1024).toFixed(1)} MiB`;
    if (gib < 1024) return `${gib.toFixed(1)} GiB`;
    return `${(gib / 1024).toFixed(1)} TiB`;
};

const formatInodes = (inodes: number) => {
    if (inodes < 1000) return inodes.toString();
    if (inodes < 1000000) return `${(inodes / 1000).toFixed(1)}K`;
    if (inodes < 1000000000) return `${(inodes / 1000000).toFixed(1)}M`;
    return `${(inodes / 1000000000).toFixed(1)}B`;
};

const getStateColor = (state: string) => {
    switch (state) {
        case 'running': return 'var(--metro-success)';
        case 'paused': return 'var(--metro-warning)';
        case 'restarting': return 'var(--metro-warning)';
        default: return 'var(--metro-error)';
    }
};

const getHealthColor = (health: string) => {
    switch (health) {
        case 'healthy': return 'var(--metro-success)';
        case 'starting': return 'var(--metro-warning)';
        case 'no_healthcheck': return 'var(--metro-text-secondary)';
        default: return 'var(--metro-error)';
    }
};

interface NetdataCardProps {
    netdataUrl: string;
    serverName: string;
    onMetrics?: (serverName: string, docker_containers: DockerContainer[], disks: { space: { used: number; available: number } }[]) => void;
}

const NetdataCard: React.FC<NetdataCardProps> = ({ netdataUrl, serverName, onMetrics }) => {
    const [metrics, setMetrics] = useState<NetdataServerData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Separate useEffect for data fetching - only depends on netdataUrl
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchNetdataMetrics(netdataUrl);
                setMetrics(data);
                setError(null);
            } catch (err) {
                setError('Failed to fetch Netdata metrics');
                console.error('Error fetching Netdata metrics:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, [netdataUrl]);

    // Separate useEffect for metrics reporting - only runs when metrics change
    useEffect(() => {
        if (metrics && onMetrics) {
            onMetrics(
                serverName,
                (metrics.docker_containers || []).map(c => ({
                    name: c.name,
                    image: c.image,
                    state: c.state,
                    health: c.health
                })),
                (metrics.system?.disks || [])
                    .filter(disk => disk.space && typeof disk.space.used === 'number' && typeof disk.space.available === 'number')
                    .map(disk => ({ space: { used: disk.space!.used, available: disk.space!.available } }))
            );
        }
    }, [metrics, serverName, onMetrics]);

    // Memoized system metrics to prevent unnecessary re-renders
    const systemMetrics = useMemo(() => {
        if (!metrics?.system) return null;
        
        return {
            cpu_usage: metrics.system.cpu_usage || 0,
            ram_usage: metrics.system.ram_usage || 0,
            ram_total: metrics.system.ram_total || 0,
            load: metrics.system.load || { load1: 0, load5: 0, load15: 0 }
        };
    }, [metrics?.system]);

    if (loading) {
        return <div style={styles.card}>Loading Netdata metrics...</div>;
    }

    if (error) {
        return <div style={styles.card}>
            <div style={styles.error}>{error}</div>
        </div>;
    }

    if (!metrics) {
        return <div style={styles.card}>No metrics available</div>;
    }

    return (
        <div style={styles.card}>
            <div style={styles.header}>
                <h3 style={styles.title}>System & Docker Metrics</h3>
                {metrics.info && (
                    <div style={styles.version}>
                        {metrics.info.application} {metrics.info.version}
                    </div>
                )}
            </div>

            {/* System Metrics Overview */}
            <div style={styles.metricsGrid}>
                {/* CPU Usage */}
                <div style={styles.metricCard}>
                    <h4 style={styles.metricTitle}>CPU Usage</h4>
                    <p style={styles.metricValue}>{(systemMetrics?.cpu_usage || 0).toFixed(1)}%</p>
                    <div style={styles.progressBar}>
                        <div 
                            style={{
                                ...styles.progressFill,
                                width: `${systemMetrics?.cpu_usage || 0}%`
                            }}
                        />
                    </div>
                </div>

                {/* Load Average */}
                <div style={styles.metricCard}>
                    <h4 style={styles.metricTitle}>Load Average</h4>
                    <p style={styles.metricValue}>{(systemMetrics?.load?.load1 || 0).toFixed(2)}</p>
                    <div style={styles.ioStats}>
                        <div style={styles.ioStat}>
                            <span style={styles.ioValue}>
                                {(systemMetrics?.load?.load5 || 0).toFixed(2)}
                            </span>
                            <span style={styles.ioLabel}>5 min</span>
                        </div>
                        <div style={styles.ioStat}>
                            <span style={styles.ioValue}>
                                {(systemMetrics?.load?.load15 || 0).toFixed(2)}
                            </span>
                            <span style={styles.ioLabel}>15 min</span>
                        </div>
                    </div>
                </div>

                {/* RAM Usage */}
                <div style={styles.metricCard}>
                    <h4 style={styles.metricTitle}>RAM Usage</h4>
                    <p style={styles.metricValue}>
                        {formatBytes((systemMetrics?.ram_usage || 0) * 1024 * 1024)}
                    </p>
                    <p style={styles.metricSubValue}>
                        of {formatBytes((systemMetrics?.ram_total || 0) * 1024 * 1024)}
                    </p>
                    <div style={styles.progressBar}>
                        <div 
                            style={{
                                ...styles.progressFill,
                                width: `${((systemMetrics?.ram_usage || 0) / (systemMetrics?.ram_total || 1)) * 100}%`
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Disk Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginTop: '20px'
            }}>
                {metrics?.system?.disks
                    .filter(disk => disk.space && disk.space.used !== undefined)
                    .map((disk) => (
                    <div key={disk.device} style={styles.metricCard}>
                        <h4 style={styles.metricTitle}>
                            {disk.device} {disk.model ? `(${disk.model})` : ''}
                        </h4>
                        <p style={styles.metricSubValue}>
                            {disk.mount_point}
                            {disk.space && ` - ${disk.space.filesystem}`}
                        </p>
                        
                        {/* Disk Space information */}
                        {disk.space && (
                            <>
                                <div style={{ marginTop: '10px', marginBottom: '5px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                        <span style={{ fontSize: '12px', color: 'var(--metro-text-secondary)' }}>
                                            Space Used: {formatGiB(disk.space.used)} of {formatGiB(disk.space.used + disk.space.available)}
                                        </span>
                                        <span style={{ fontSize: '12px', color: 'var(--metro-text-secondary)' }}>
                                            {((disk.space.used / (disk.space.used + disk.space.available)) * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                    <div style={styles.progressBar}>
                                        <div 
                                            style={{
                                                ...styles.progressFill,
                                                width: `${(disk.space.used / (disk.space.used + disk.space.available)) * 100}%`
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                        <span style={{ fontSize: '12px', color: 'var(--metro-text-secondary)' }}>
                                            Inodes Used: {formatInodes(disk.space.inodes_used)} of {formatInodes(disk.space.inodes_used + disk.space.inodes_available)}
                                        </span>
                                        <span style={{ fontSize: '12px', color: 'var(--metro-text-secondary)' }}>
                                            {((disk.space.inodes_used / (disk.space.inodes_used + disk.space.inodes_available)) * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                    <div style={styles.progressBar}>
                                        <div 
                                            style={{
                                                ...styles.progressFill,
                                                width: `${(disk.space.inodes_used / (disk.space.inodes_used + disk.space.inodes_available)) * 100}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {/* Docker Container Section */}
            <div style={styles.chartContainer}>
                <h4>Docker Containers</h4>
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                    gap: '15px' 
                }}>
                    {metrics?.docker_containers?.map(container => (
                        <div key={container.name} style={{ 
                            padding: '15px', 
                            backgroundColor: 'var(--metro-dark)', 
                            borderRadius: '8px',
                            border: '1px solid var(--metro-card-border)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '8px'
                            }}>
                                <div style={{ fontSize: '14px', color: 'var(--metro-text)' }}>
                                    {container.name}
                                </div>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '8px' 
                                }}>
                                    <div style={{ 
                                        padding: '2px 6px', 
                                        borderRadius: '4px', 
                                        backgroundColor: getStateColor(container.state),
                                        fontSize: '12px',
                                        color: 'var(--metro-dark)'
                                    }}>
                                        {container.state}
                                    </div>
                                </div>
                            </div>
                            <div style={{ 
                                fontSize: '12px', 
                                color: 'var(--metro-text-secondary)',
                                wordBreak: 'break-all' as const
                            }}>
                                {container.image}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NetdataCard;
